<template>
    <Sidebar
      @open-create-modal="openCreateModal"
      @logout="logout"
    />

    <!-- Main Content -->
    <div class="lg:ml-64 min-h-screen bg-gradient-to-br from-gray-50 to-blue-50">
      <div class="p-4 lg:p-8 pt-16 lg:pt-8">
        <!-- Header -->
        <div class="mb-8">
          <h1 class="text-3xl lg:text-4xl font-bold text-gray-900 mb-2">Financial Overview</h1>
          <p class="text-gray-600">Track your organization's financial metrics and request statistics</p>
        </div>
  
        <!-- Statistics Grid -->
        <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-3 gap-4 lg:gap-6">
          <!-- Total Disbursed -->
          <div class="stat-card group hover:shadow-xl hover:-translate-y-1">
            <div class="flex items-center justify-between mb-4">
              <div class="stat-icon bg-gradient-to-r from-blue-500 to-blue-600">
                <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1"/>
                </svg>
              </div>
              <div class="text-xs font-medium px-2 py-1 bg-blue-100 text-blue-700 rounded-full">
                Total
              </div>
            </div>
            <h3 class="text-sm font-semibold text-gray-600 mb-2">Amount Disbursed</h3>
            <p class="text-2xl lg:text-3xl font-bold text-gray-900 mb-1">
              {{ formatCurrency(totalDisbursed) }}
            </p>
            <div class="flex items-center text-sm">
              <span class="text-green-600 font-medium">+12.5%</span>
              <span class="text-gray-500 ml-1">from last month</span>
            </div>
          </div>
  
          <!-- Weekly Disbursed -->
          <div class="stat-card group hover:shadow-xl hover:-translate-y-1">
            <div class="flex items-center justify-between mb-4">
              <div class="stat-icon bg-gradient-to-r from-emerald-500 to-emerald-600">
                <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"/>
                </svg>
              </div>
              <div class="text-xs font-medium px-2 py-1 bg-emerald-100 text-emerald-700 rounded-full">
                Weekly
              </div>
            </div>
            <h3 class="text-sm font-semibold text-gray-600 mb-2">This Week</h3>
            <p class="text-2xl lg:text-3xl font-bold text-gray-900 mb-1">
              {{ formatCurrency(weeklyDisbursed) }}
            </p>
            <div class="flex items-center text-sm">
              <span class="text-emerald-600 font-medium">+8.2%</span>
              <span class="text-gray-500 ml-1">from last week</span>
            </div>
          </div>
  
          <!-- Total Requested -->
          <div class="stat-card group hover:shadow-xl hover:-translate-y-1">
            <div class="flex items-center justify-between mb-4">
              <div class="stat-icon bg-gradient-to-r from-purple-500 to-purple-600">
                <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"/>
                </svg>
              </div>
              <div class="text-xs font-medium px-2 py-1 bg-purple-100 text-purple-700 rounded-full">
                Requests
              </div>
            </div>
            <h3 class="text-sm font-semibold text-gray-600 mb-2">Total Requested</h3>
            <p class="text-2xl lg:text-3xl font-bold text-gray-900 mb-1">
              {{ formatCurrency(totalRequested) }}
            </p>
            <div class="flex items-center text-sm">
              <span class="text-purple-600 font-medium">{{ allRequests.length }}</span>
              <span class="text-gray-500 ml-1">total requests</span>
            </div>
          </div>
  
          <!-- Total Approved -->
          <div class="stat-card group hover:shadow-xl hover:-translate-y-1">
            <div class="flex items-center justify-between mb-4">
              <div class="stat-icon bg-gradient-to-r from-green-500 to-green-600">
                <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/>
                </svg>
              </div>
              <div class="text-xs font-medium px-2 py-1 bg-green-100 text-green-700 rounded-full">
                Approved
              </div>
            </div>
            <h3 class="text-sm font-semibold text-gray-600 mb-2">Total Approved</h3>
            <p class="text-2xl lg:text-3xl font-bold text-gray-900 mb-1">
              {{ formatCurrency(totalApproved) }}
            </p>
            <div class="flex items-center text-sm">
              <span class="text-green-600 font-medium">{{ getApprovalRate() }}%</span>
              <span class="text-gray-500 ml-1">approval rate</span>
            </div>
          </div>
          <div v-if="showCreateModal" class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div class="bg-white bg-opacity-90 backdrop-blur-lg rounded-lg p-6 w-full max-w-md">
            <CreateRequest @request-created="fetchRequestsAndCloseModal" @close="closeCreateModal" />
          </div>
        </div>
  
          <!-- Total Rejected -->
          <div class="stat-card group hover:shadow-xl hover:-translate-y-1">
            <div class="flex items-center justify-between mb-4">
              <div class="stat-icon bg-gradient-to-r from-red-500 to-red-600">
                <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
                </svg>
              </div>
              <div class="text-xs font-medium px-2 py-1 bg-red-100 text-red-700 rounded-full">
                Rejected
              </div>
            </div>
            <h3 class="text-sm font-semibold text-gray-600 mb-2">Total Rejected</h3>
            <p class="text-2xl lg:text-3xl font-bold text-gray-900 mb-1">
              {{ formatCurrency(totalRejected) }}
            </p>
            <div class="flex items-center text-sm">
              <span class="text-red-600 font-medium">{{ getRejectedCount() }}</span>
              <span class="text-gray-500 ml-1">requests rejected</span>
            </div>
          </div>
  
          <!-- Total Pending -->
          <div class="stat-card group hover:shadow-xl hover:-translate-y-1">
            <div class="flex items-center justify-between mb-4">
              <div class="stat-icon bg-gradient-to-r from-yellow-500 to-yellow-600">
                <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"/>
                </svg>
              </div>
              <div class="text-xs font-medium px-2 py-1 bg-yellow-100 text-yellow-700 rounded-full">
                Pending
              </div>
            </div>
            <h3 class="text-sm font-semibold text-gray-600 mb-2">Total Pending</h3>
            <p class="text-2xl lg:text-3xl font-bold text-gray-900 mb-1">
              {{ formatCurrency(totalPending) }}
            </p>
            <div class="flex items-center text-sm">
              <span class="text-yellow-600 font-medium">{{ getPendingCount() }}</span>
              <span class="text-gray-500 ml-1">awaiting approval</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  </template>
  
  <script>
  import axios from 'axios';
  import CreateRequest from './CreateRequest.vue';
  import Sidebar from './Sidebar.vue';

  export default {
    components: {CreateRequest, Sidebar},
    data() {
      return {
        user: JSON.parse(localStorage.getItem('user') || '{}'),
        allRequests: [],
        totalDisbursed: 0,
        weeklyDisbursed: 0,
        totalRequested: 0,
        totalApproved: 0,
        totalRejected: 0,
        totalPending: 0,
        showCreateModal: false,
      };
    },
    async mounted() {
      await this.fetchAllRequests();
      this.calculateStatistics();
    },
    methods: {
      async fetchAllRequests() {
        const token = localStorage.getItem('token');
        const headers = { Authorization: `Bearer ${token}` };
        try {
          const res = await axios.get('https://guarantee.iwacufinance.com/cash-request/api/requests/requests', { headers });
          this.allRequests = res.data;
          console.log(this.allRequests);
        } catch (error) {
          console.error('Error fetching all requests:', error);
        }
      },
      getDisbursementDate(request) {
        const approvedSteps = request.approvalPath.filter(path => path.status === 'approved');
        if (approvedSteps.length === request.approvalPath.length) {
          return new Date(Math.max(...approvedSteps.map(step => new Date(step.timestamp))));
        }
        return null;
      },
      calculateStatistics() {
        const now = new Date('2025-06-18T13:02:00Z');
        const oneWeekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
  
        this.totalRequested = this.allRequests.reduce((sum, request) => sum + request.amount, 0);
  
        const approvedRequests = this.allRequests.filter(request => request.status === 'approved');
        this.totalApproved = approvedRequests.reduce((sum, request) => sum + request.amount, 0);
        this.totalDisbursed = this.totalApproved;
  
        this.weeklyDisbursed = approvedRequests
          .filter(request => {
            const disbursementDate = this.getDisbursementDate(request);
            return disbursementDate && disbursementDate >= oneWeekAgo;
          })
          .reduce((sum, request) => sum + request.amount, 0);
  
        this.totalRejected = this.allRequests
          .filter(request => request.status === 'rejected')
          .reduce((sum, request) => sum + request.amount, 0);
  
        this.totalPending = this.allRequests
          .filter(request => request.status === 'pending')
          .reduce((sum, request) => sum + request.amount, 0);
      },
      formatCurrency(amount) {
        return new Intl.NumberFormat('en-RW', {
          style: 'currency',
          currency: 'RWF',
          minimumFractionDigits: 0,
          maximumFractionDigits: 0,
        }).format(amount);
      },
      getApprovalRate() {
        if (this.allRequests.length === 0) return 0;
        const approvedCount = this.allRequests.filter(r => r.status === 'approved').length;
        return Math.round((approvedCount / this.allRequests.length) * 100);
      },
      getRejectedCount() {
        return this.allRequests.filter(r => r.status === 'rejected').length;
      },
      getPendingCount() {
        return this.allRequests.filter(r => r.status === 'pending').length;
      },
      openCreateModal() {
      this.showCreateModal = true;
    },
    closeCreateModal() {
      this.showCreateModal = false;
    },
    fetchRequestsAndCloseModal() {
        this.closeCreateModal();
      },
      logout() {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      this.$router.push('/');
    },
    }
  };
  </script>

<style scoped>
  .stat-card {
    @apply bg-white rounded-2xl p-6 shadow-sm border border-gray-100 transition-all duration-300;
  }

  .stat-icon {
    @apply w-12 h-12 rounded-xl text-white flex items-center justify-center shadow-lg;
  }

  .action-btn {
    @apply flex items-center justify-center px-6 py-3 rounded-xl font-semibold transition-all duration-200 shadow-sm hover:shadow-md;
  }
  </style>